import { CalendarBlank } from "@phosphor-icons/react";
import { useState } from "react";
import type { DateRange as DayPickerRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export type DateRange = {
	start_date: string;
	end_date: string;
};

type Props = {
	value: DateRange;
	onChange: (range: DateRange) => void;
};

const PRESETS = [7, 30, 90] as const;
export type PresetDays = (typeof PRESETS)[number];

function toIsoDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

export function presetRange(days: number): DateRange {
	const end = new Date();
	const start = new Date();
	start.setDate(start.getDate() - days);
	return { start_date: toIsoDate(start), end_date: toIsoDate(end) };
}

export function getDefaultDateRange(): DateRange {
	return presetRange(30);
}

export function parseLocalDate(str: string): Date {
	return new Date(`${str}T00:00:00`);
}

export function getActivePreset(value: DateRange): PresetDays | null {
	for (const days of PRESETS) {
		const preset = presetRange(days);
		if (
			value.start_date === preset.start_date &&
			value.end_date === preset.end_date
		) {
			return days;
		}
	}
	return null;
}

export function DateRangePicker({ value, onChange }: Props) {
	const { t, i18n } = useTranslation();
	const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
	const [open, setOpen] = useState(false);

	const selected: DayPickerRange = {
		from: parseLocalDate(value.start_date),
		to: parseLocalDate(value.end_date),
	};

	function handleSelect(range: DayPickerRange | undefined) {
		if (!range?.from) return;
		if (range.to) {
			onChange({
				start_date: toIsoDate(range.from),
				end_date: toIsoDate(range.to),
			});
		} else {
			onChange({
				start_date: toIsoDate(range.from),
				end_date: toIsoDate(range.from),
			});
		}
	}

	const sameMonth =
		selected.from &&
		selected.to &&
		selected.from.getFullYear() === selected.to.getFullYear() &&
		selected.from.getMonth() === selected.to.getMonth();

	const label =
		selected.from && selected.to
			? `${dateFormatter.format(selected.from)} – ${dateFormatter.format(selected.to)}`
			: t("dashboard.dateRange.from");

	const activePreset = getActivePreset(value);

	return (
		<div className="bg-background flex items-center gap-1 rounded-md border p-1">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="text-foreground hover:bg-muted flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-normal"
					>
						<CalendarBlank
							className="text-muted-foreground size-4"
							aria-hidden="true"
						/>
						{label}
					</button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						mode="range"
						defaultMonth={selected.from}
						selected={selected}
						onSelect={handleSelect}
						numberOfMonths={sameMonth ? 1 : 2}
						disabled={{ after: new Date() }}
					/>
				</PopoverContent>
			</Popover>
			<Separator orientation="vertical" className="h-5" />
			{PRESETS.map((days) => {
				const active = activePreset === days;
				return (
					<button
						key={days}
						type="button"
						onClick={() => onChange(presetRange(days))}
						className={cn(
							"rounded-md px-2.5 py-1 text-xs transition-colors",
							active
								? "bg-primary/10 text-primary font-medium"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						{t(`dashboard.dateRange.preset${days}Short` as const)}
					</button>
				);
			})}
		</div>
	);
}
